import { type RefObject, useEffect, useRef, useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ASK_ASSISTANTS,
  type AskAssistant,
  pasteShortcut,
} from "./askAssistant";
import { formatTime } from "./formatTime";
import type { MeetingClock, TimelineCue } from "./meetingTimeline";
import { transcriptText } from "./transcriptText";

// Cues arrive in meeting time (see meetingTimeline), every recorded part's
// transcript in order as one list, so the times shown, the clock and a click all
// agree. `highlightTime` is meeting time too; callers convert stored offsets.
export const TranscriptPanel = ({
  videoRef,
  cues,
  videoKey,
  highlightTime,
}: {
  videoRef: RefObject<MeetingClock | null>;
  cues: TimelineCue[];
  videoKey?: number;
  highlightTime?: number;
}) => {
  const [activeIndex, setActiveIndex] = useState(-1);
  const [copied, setCopied] = useState(false);
  // What the last Ask click did, kept on screen until dismissed. The button's own
  // label is no use for this: by the time it changes, focus is in the new tab,
  // and Bill came back to a button that had already reset (LCCRM-88).
  const [notice, setNotice] = useState<
    { kind: "asked"; assistant: string } | { kind: "copy_failed" } | null
  >(null);
  const activeRef = useRef<HTMLDivElement>(null);
  const paste = pasteShortcut();

  // Bill takes transcripts into a chat assistant because Zoom's summaries read
  // dry. This hands him the text without timestamps. LCATOMICZO-52.
  const handleCopy = async () => {
    setNotice(null);
    try {
      await navigator.clipboard.writeText(transcriptText(cues));
    } catch {
      setNotice({ kind: "copy_failed" });
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Copies, then opens a fresh chat: one paste instead of a copy, a navigation
  // and a paste. See askAssistant.ts for why the transcript cannot go in the URL.
  // If the copy fails the tab stays closed, since an empty chat is the outcome
  // that would make him think the button is broken.
  const handleAsk = async (assistant: AskAssistant) => {
    try {
      await navigator.clipboard.writeText(transcriptText(cues));
    } catch {
      setNotice({ kind: "copy_failed" });
      return;
    }
    setNotice({ kind: "asked", assistant: assistant.name });
    window.open(assistant.url, "_blank", "noopener,noreferrer");
  };

  // Pin the cue at a given VTT time (e.g. the task's moment) — distinct from
  // the moving active-playback highlight. Match the active logic, with an
  // at-or-before fallback for integer rounding / trailing gaps.
  let highlightIndex = -1;
  if (highlightTime != null && cues.length) {
    highlightIndex = cues.findIndex(
      (c) => highlightTime >= c.start && highlightTime < c.end,
    );
    if (highlightIndex === -1) {
      for (let i = cues.length - 1; i >= 0; i--) {
        if (cues[i].start <= highlightTime) {
          highlightIndex = i;
          break;
        }
      }
    }
  }

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onTimeUpdate = () => {
      const t = video.currentTime;
      const idx = cues.findIndex((c) => t >= c.start && t < c.end);
      setActiveIndex(idx);
    };
    video.addEventListener("timeupdate", onTimeUpdate);
    return () => video.removeEventListener("timeupdate", onTimeUpdate);
  }, [videoRef, cues, videoKey]);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [activeIndex]);

  if (cues.length === 0) return null;

  return (
    <div className="border rounded-lg overflow-hidden flex flex-col h-full">
      <div className="flex items-center gap-2 p-2 bg-muted/50 border-b shrink-0">
        <span className="text-sm font-medium text-muted-foreground flex-1">
          Transcript
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className="text-xs px-2 py-1 rounded border bg-background hover:bg-muted transition-colors"
        >
          {copied ? "Copied" : "Copy"}
        </button>
        {ASK_ASSISTANTS.map((assistant) => (
          <button
            key={assistant.key}
            type="button"
            onClick={() => handleAsk(assistant)}
            title={`Copies the transcript and opens ${assistant.name}. Paste it there with ${paste}.`}
            className="text-xs px-2 py-1 rounded border bg-background hover:bg-muted transition-colors"
          >
            Ask {assistant.name}
          </button>
        ))}
      </div>
      {notice && (
        <div
          role="status"
          className={`flex items-center gap-2 px-3 py-2 border-b text-sm shrink-0 ${
            notice.kind === "asked"
              ? "bg-primary/10"
              : "bg-destructive/10 text-destructive"
          }`}
        >
          <span className="flex-1">
            {notice.kind === "asked"
              ? `Transcript copied. In the ${notice.assistant} tab, press ${paste}, add your notes, then Enter.`
              : "Couldn't copy the transcript. Check that this site may use the clipboard, then try again."}
          </span>
          <button
            type="button"
            onClick={() => setNotice(null)}
            aria-label="Dismiss"
            className="text-muted-foreground hover:text-foreground px-1"
          >
            ×
          </button>
        </div>
      )}
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-2 space-y-1">
          {cues.map((cue, i) => (
            <div
              key={i}
              ref={i === activeIndex ? activeRef : undefined}
              onClick={() => {
                if (videoRef.current) {
                  videoRef.current.currentTime = cue.start;
                  videoRef.current.play();
                }
              }}
              className={`flex gap-2 p-1.5 rounded cursor-pointer text-sm transition-colors ${
                i === highlightIndex
                  ? "bg-amber-500/15 ring-1 ring-inset ring-amber-500/60 font-medium"
                  : i === activeIndex
                    ? "bg-primary/10 font-medium"
                    : "hover:bg-muted/50"
              }`}
            >
              <span className="text-muted-foreground shrink-0 font-mono text-xs mt-0.5">
                {formatTime(cue.start)}
              </span>
              <span>{cue.text}</span>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};
